import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ShortcutsHelp from "./components/ui/shortcuts-help";
import GlobalBrainDump from "./components/GlobalBrainDump";
import { isSuperAdmin } from "./hooks/useSuperAdmin";
import TeamMemberPage from "./pages/TeamMemberPage";

// Pages
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import LandingPage from "./pages/LandingPage";
import CalendarPage from "./pages/CalendarPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import AdminBugsPage from "./pages/AdminBugsPage";
import AdminTasksPage from "./pages/AdminTasksPage";
import MarketingPage from "./pages/MarketingPage";
import MarketingPageNew from "./pages/MarketingPageNew";
import BizDevDashboard from "./pages/BizDevDashboard";
import BizDevLeads from "./pages/BizDevLeads";
import BizDevLeadDetail from "./pages/BizDevLeadDetail";
import BizDevBriefs from "./pages/BizDevBriefs";
import BizDevBriefDetail from "./pages/BizDevBriefDetail";
import BizDevFunnel from "./pages/BizDevFunnel";
import BizDevAccounts from "./pages/BizDevAccounts";
import BriefsKanban from "./pages/BriefsKanban";
import ErrorBoundary from "./components/ErrorBoundary";
import BizDevAccountDetail from "./pages/BizDevAccountDetail";
import ProductionDashboard from "./pages/ProductionDashboard";
import RoutingDashboard from "./pages/RoutingDashboard";
import ProductionBackfill from "./pages/ProductionBackfill";
import ProductionPerformance from "./pages/ProductionPerformance";
import AssetDetailPage from "./pages/AssetDetailPage";
// Production Tracker (native module) — brief->delivered board + daily-ops layer.
import PtrackerMyDay from "./pages/ptracker/PtrackerMyDay";
import PtrackerPipeline from "./pages/ptracker/PtrackerPipeline";
import PtrackerQueue from "./pages/ptracker/PtrackerQueue";
import PtrackerProductivity from "./pages/ptracker/PtrackerProductivity";
import PtrackerTasks from "./pages/ptracker/PtrackerTasks";
import PtrackerShoots from "./pages/ptracker/PtrackerShoots";
import PtrackerCalendar from "./pages/ptracker/PtrackerCalendar";
import PtrackerAllocation from "./pages/ptracker/PtrackerAllocation";
import PtrackerSettings from "./pages/ptracker/PtrackerSettings";
import PtrackerAttendance from "./pages/ptracker/PtrackerAttendance";
import PtrackerRisks from "./pages/ptracker/PtrackerRisks";
import ReviewsAdmin from "./pages/ReviewsAdmin";
import ReviewInstancePage from "./pages/ReviewInstancePage";
import ReviewsInbox from "./pages/ReviewsInbox";
import MyScorecard from "./pages/MyScorecard";
import ReviewWorkLog from "./pages/ReviewWorkLog";
import MicrositesDirectory from "./pages/MicrositesDirectory";
import RetainerHealth from "./pages/RetainerHealth";
// Reviews is company-wide; only the manager surfaces (cycle admin, retainer &
// campaign log, retainer health) are role-gated. Admins bypass in ProtectedRoute.
const REVIEW_ADMIN_ROLES = ["admin", "srmanager", "manager", "creativedirector"];
import ProductionStudios from "./pages/ProductionStudios";
import ProductionCrew from "./pages/ProductionCrew";
import ProductionProjects from "./pages/ProductionProjects";
import ProductionShoots from "./pages/ProductionShoots";
import ProfileSetupPage from "./pages/ProfileSetupPage";
import MyDashboardPage from "./pages/MyDashboardPage";
import RecruitingPage from "./pages/RecruitingPage";
import ScriptsPage from "./pages/ScriptsPage";
import TeamProfilePage from "./pages/TeamProfilePage";
import CreateTaskPage from "./pages/CreateTaskPage";
import CreateProjectPage from "./pages/CreateProjectPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ProjectsListPage from "./pages/ProjectsListPage";
import TasksListPage from "./pages/TasksListPage";
import SearchPage from "./pages/SearchPage";
import FixReviewersPage from "./pages/FixReviewersPage";
import PitchDeckLibrary from "./pages/PitchDeckLibrary";
import LibraryDetailPage from "./pages/LibraryDetailPage";
import Onboarding from "./pages/Onboarding";
import AdminOnboarding from "./pages/AdminOnboarding";
import TaskDetailPage from "./pages/TaskDetailPage";
import SetPasswordPage from './pages/SetPasswordPage';
import BugReportWidget from './components/BugReportWidget';
import AdminTaskWidget from './components/AdminTaskWidget';

import GlobalSearch from "./components/GlobalSearch";
import AppShell from "./components/AppShell";
import { HintsProvider } from "./components/HintsContext";
import DMShell from "./components/DMShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// API instance with auth header and cookies
export const api = axios.create({
  baseURL: API,
  withCredentials: true, // Important for cookies
});

api.interceptors.request.use((config) => {
  // Send token from localStorage or cookie handles auth
  const token = localStorage.getItem("dreamteam_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("dreamteam_token");
      localStorage.removeItem("dreamteam_user");
      // Don't redirect if already on login page
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth Callback Component for Google OAuth
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (processed) return;
    setProcessed(true);

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/login");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await api.post("/auth/google/session", { session_id: sessionId });
        const { user } = response.data;
        
        // Update auth context state (not just localStorage)
        updateUser(user);
        toast.success(`Welcome back, ${user.name}!`);
        // Restore deep link or go to /me
        const stored = sessionStorage.getItem("dreamteam_redirect");
        sessionStorage.removeItem("dreamteam_redirect");
        navigate(stored || "/me", { replace: true });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate, processed, updateUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-pulse text-primary text-2xl font-heading mb-4">
          AUTHENTICATING...
        </div>
        <p className="text-text-muted">Hang tight, superstar! 🌟</p>
      </div>
    </div>
  );
}

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check for stored user
    const storedUser = localStorage.getItem("dreamteam_user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        // Refresh user data from backend
        try {
          const res = await api.get("/auth/me");
          setUser(res.data);
          localStorage.setItem("dreamteam_user", JSON.stringify(res.data));
        } catch (e) {
          if (e.response?.status === 401) {
            localStorage.removeItem("dreamteam_token");
            localStorage.removeItem("dreamteam_user");
            setUser(null);
          }
        }
      } catch (e) {
        localStorage.removeItem("dreamteam_user");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Store intended route for deep link restore
    const intendedPath = window.location.pathname !== "/login" ? window.location.pathname : "/me";
    sessionStorage.setItem("dreamteam_redirect", intendedPath);
    const redirectUrl = window.location.origin + '/me';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("dreamteam_token", res.data.token);
    localStorage.setItem("dreamteam_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.post("/auth/register", data);
    localStorage.setItem("dreamteam_token", res.data.token);
    localStorage.setItem("dreamteam_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem("dreamteam_token");
    localStorage.removeItem("dreamteam_user");
    setUser(null);
    toast.success("See you soon, superstar! 👋");
    window.location.href = "/login";
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("dreamteam_user", JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      loginWithGoogle,
      register, 
      logout, 
      updateUser, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Hard browser redirect — leaves the React app entirely. Used to bounce
// short-form client microsite URLs (e.g. /FWD) into the FastAPI-served
// password gate (/api/fwd/). Setting window.location escapes the SPA's
// router so the request actually hits the backend.
function HardRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-white/30 font-mono text-sm">Loading…</div></div>;
}

// Redirect authenticated users to /me, show marketing page to visitors
function AuthRedirectOrMarketing() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="animate-pulse text-white/30 font-heading text-xl">LOADING...</div></div>;
  if (user) return <Navigate to="/me" replace />;
  return <MarketingPage />;
}

// Pending Approval Screen
function PendingApproval() {
  const { user, logout } = useAuth();
  const isRevoked = user?.status === "revoked";
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="pending-approval">
      <div className="glass-card rounded-2xl p-8 max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
          <span className="text-3xl">{isRevoked ? "🚫" : "⏳"}</span>
        </div>
        <h2 className="font-heading text-2xl text-white mb-2">
          {isRevoked ? "ACCESS REVOKED" : "PENDING APPROVAL"}
        </h2>
        <p className="text-text-secondary text-sm mb-6 normal-case font-normal">
          {isRevoked
            ? "Your access has been revoked. Contact admin for assistance."
            : `Hey ${user?.name || "there"}! Your account is awaiting admin approval. You'll be notified once you're in.`}
        </p>
        <p className="text-text-muted text-xs mb-6 normal-case font-normal">Logged in as {user?.email}</p>
        <button onClick={logout} className="px-6 py-2.5 glass rounded-full text-white/60 text-sm hover:text-white hover:bg-white/10 transition-colors">
          Sign Out
        </button>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [], allowedEmails = [], skipProfileCheck = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const roleKey = (r) => (r || "").toLowerCase();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-2xl font-heading">LOADING...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isAdmin = roleKey(user.role) === "admin" || isSuperAdmin(user.email);
  if (!isAdmin && user.status !== "approved") {
    return <PendingApproval />;
  }

  // Profile completion gate. A member must have a name, a designation AND a
  // reporting manager (reports_to) — or the explicit profile_completed flag set
  // when they submit the setup form (which covers the "no manager" / top-of-org
  // case). Anyone missing a manager is sent to /profile/setup to pick one once.
  const profileDone = !!(user.profile_completed || (user.designation && (user.full_name || user.name) && user.reports_to));
  if (!skipProfileCheck && !profileDone && location.pathname !== "/profile/setup") {
    return <Navigate to="/profile/setup" replace />;
  }

  if (allowedRoles.length > 0 && !isAdmin) {
    const userRole = roleKey(user.role);
    const hasRole = allowedRoles.some(r => roleKey(r) === userRole);
    if (!hasRole) {
      return <Navigate to="/me" replace />;
    }
  }

  // Email allowlist — a hard gate with NO admin bypass (used to pilot a
  // feature with specific accounts before wider rollout).
  if (allowedEmails.length > 0) {
    const emailLc = (user.email || "").toLowerCase();
    const ok = allowedEmails.some(e => e.toLowerCase() === emailLc);
    if (!ok) {
      return <Navigate to="/me" replace />;
    }
  }

  return <AppShell>{children}</AppShell>;
}

// Main App Layout
function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-background">
      {children}
    </div>
  );
}

// App Router with Auth Callback handling
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<AuthRedirectOrMarketing />} />
      <Route path="/__v2preview" element={<MarketingPageNew />} />
      {/* Client microsites — gated under /api/* so FastAPI handles them.
          We catch the short-form URLs here and hard-redirect to the
          actual handler so links shared as `/FWD` (the URL the client
          was sent) still land on the password gate. */}
      <Route path="/FWD" element={<HardRedirect to="/api/fwd/" />} />
      <Route path="/FWD/*" element={<HardRedirect to="/api/fwd/" />} />
      <Route path="/PhonePe" element={<HardRedirect to="/api/phonepe/" />} />
      <Route path="/PhonePe/*" element={<HardRedirect to="/api/phonepe/" />} />
      <Route path="/dew-it" element={<HardRedirect to="/api/dew-it/" />} />
      <Route path="/dew-it/*" element={<HardRedirect to="/api/dew-it/" />} />
      <Route path="/DewIt" element={<HardRedirect to="/api/dew-it/" />} />
      <Route path="/DewIt/*" element={<HardRedirect to="/api/dew-it/" />} />
      <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/profile/setup" element={<ProtectedRoute skipProfileCheck={true}><ProfileSetupPage /></ProtectedRoute>} />
      <Route path="/me" element={<ProtectedRoute><MyDashboardPage /></ProtectedRoute>} />
      <Route path="/recruiting" element={<ProtectedRoute><RecruitingPage /></ProtectedRoute>} />
      <Route path="/scripts" element={<ProtectedRoute><ScriptsPage /></ProtectedRoute>} />
      <Route path="/team/:userId" element={<ProtectedRoute><TeamProfilePage /></ProtectedRoute>} />
      <Route path="/tasks/new" element={<ProtectedRoute><CreateTaskPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsListPage /></ProtectedRoute>} />
      <Route path="/projects/new" element={<ProtectedRoute><CreateProjectPage /></ProtectedRoute>} />
        <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/fix-reviewers" element={<ProtectedRoute><FixReviewersPage /></ProtectedRoute>} />
      <Route path="/pitch-decks" element={<ProtectedRoute><PitchDeckLibrary /></ProtectedRoute>} />
      <Route path="/library/:deckId" element={<ProtectedRoute><LibraryDetailPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/admin/onboarding" element={<ProtectedRoute allowedRoles={["admin"]}><AdminOnboarding /></ProtectedRoute>} />
      <Route path="/efl" element={<div style={{position:'fixed',inset:0,zIndex:9999,background:'#000'}}><iframe src="/efl.html" style={{width:'100%',height:'100%',border:'none'}} title="EFL Strategy Presentation" /></div>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksListPage /></ProtectedRoute>} />
      <Route path="/tasks/:taskId" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />
      {/* Performance Reviews (platform-synced 360°) — company-wide. Personal
          surfaces (own scorecard / inbox / a review you're a voice on) are open
          to every employee; the backend enforces per-record view access. The
          manager surfaces below are role-gated. */}
      <Route path="/reviews" element={<ProtectedRoute><MyScorecard /></ProtectedRoute>} />
      <Route path="/reviews/inbox" element={<ProtectedRoute><ReviewsInbox /></ProtectedRoute>} />
      <Route path="/reviews/instance/:instanceId" element={<ProtectedRoute><ReviewInstancePage /></ProtectedRoute>} />
      {/* Retainer & campaign log — account-level work with no production-asset
          equivalent (AM/GAD retainer months, IMM campaigns). Makers' videos &
          statics are entered/backfilled in Production, not here. */}
      <Route path="/reviews/work-log" element={<ProtectedRoute allowedRoles={REVIEW_ADMIN_ROLES}><ReviewWorkLog /></ProtectedRoute>} />
      <Route path="/reviews/retainer-health" element={<ProtectedRoute allowedRoles={REVIEW_ADMIN_ROLES}><RetainerHealth /></ProtectedRoute>} />
      <Route path="/microsites" element={<ProtectedRoute><MicrositesDirectory /></ProtectedRoute>} />
      <Route path="/reviews/admin" element={<ProtectedRoute allowedRoles={REVIEW_ADMIN_ROLES}><ReviewsAdmin /></ProtectedRoute>} />
      <Route path="/home" element={<Navigate to="/me" replace />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      {/* HR module routes removed — module retired Apr 20, 2026 */}
      {/* Legacy HR routes (/dashboard, /swipe, /candidates, /leaderboard, /hr/funnel,
          /openings, /bulk-import) redirect to /me so any saved links don't 404. */}
      <Route path="/dashboard" element={<Navigate to="/me" replace />} />
      <Route path="/swipe" element={<Navigate to="/me" replace />} />
      <Route path="/candidates" element={<Navigate to="/me" replace />} />
      <Route path="/candidates/:id" element={<Navigate to="/me" replace />} />
      <Route path="/leaderboard" element={<Navigate to="/me" replace />} />
      <Route path="/hr/funnel" element={<Navigate to="/me" replace />} />
      <Route path="/openings" element={<Navigate to="/me" replace />} />
      <Route path="/openings/:openingId" element={<Navigate to="/me" replace />} />
      <Route path="/bulk-import" element={<Navigate to="/me" replace />} />

      {/* Kept — general productivity surfaces, not HR-specific */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Biz Dev Module Routes — open to ALL approved users */}
      <Route path="/bizdev" element={<Navigate to="/me" replace />} />
      <Route path="/bizdev/leads" element={<ProtectedRoute><BizDevLeads /></ProtectedRoute>} />
      <Route path="/bizdev/leads/:leadId" element={<ProtectedRoute><BizDevLeadDetail /></ProtectedRoute>} />
      <Route path="/bizdev/briefs/kanban" element={<ProtectedRoute><BriefsKanban /></ProtectedRoute>} />
      <Route path="/bizdev/briefs" element={<ProtectedRoute><BizDevBriefs /></ProtectedRoute>} />
      <Route path="/bizdev/briefs/new" element={<ProtectedRoute><BizDevBriefs /></ProtectedRoute>} />
      <Route path="/bizdev/briefs/:briefId" element={<ProtectedRoute><BizDevBriefDetail /></ProtectedRoute>} />
      <Route
        path="/bizdev/funnel"
        element={
          <ProtectedRoute allowedRoles={["admin", "srmanager"]}>
            <BizDevFunnel />
          </ProtectedRoute>
        }
      />
      <Route path="/bizdev/accounts" element={<ProtectedRoute><BizDevAccounts /></ProtectedRoute>} />
      <Route path="/bizdev/accounts/:id" element={<ProtectedRoute><BizDevAccountDetail /></ProtectedRoute>} />
          <Route path="/bizdev/pipeline" element={<Navigate to="/bizdev/leads" replace />} />
      {/* Production Module Routes — open to ALL approved users */}
      <Route path="/production" element={<ProtectedRoute><ProductionDashboard /></ProtectedRoute>} />
      <Route path="/routing" element={<ProtectedRoute><RoutingDashboard /></ProtectedRoute>} />
      <Route path="/production/backfill" element={<ProtectedRoute><ProductionBackfill /></ProtectedRoute>} />
      <Route path="/production/studios" element={<ProtectedRoute><ProductionStudios /></ProtectedRoute>} />
      <Route path="/production/crew" element={<ProtectedRoute><ProductionCrew /></ProtectedRoute>} />
      <Route path="/production/projects" element={<ProtectedRoute><ProductionProjects /></ProtectedRoute>} />
      <Route path="/production/projects/:productionId" element={<ProtectedRoute><ProductionProjects /></ProtectedRoute>} />
      <Route path="/production/shoots" element={<ProtectedRoute><ProductionShoots /></ProtectedRoute>} />
      <Route path="/production/performance" element={<ProtectedRoute><ProductionPerformance /></ProtectedRoute>} />
      <Route path="/production/assets" element={<ProtectedRoute><ProductionDashboard /></ProtectedRoute>} />
      <Route path="/production/assets/:assetId" element={<ProtectedRoute><AssetDetailPage /></ProtectedRoute>} />
      {/* Production Tracker (native module) — pipeline + daily-ops productivity layer. */}
      <Route path="/ptracker/myday" element={<ProtectedRoute><PtrackerMyDay /></ProtectedRoute>} />
      <Route path="/ptracker/pipeline" element={<ProtectedRoute><PtrackerPipeline /></ProtectedRoute>} />
      <Route path="/ptracker/queue" element={<ProtectedRoute><PtrackerQueue /></ProtectedRoute>} />
      <Route path="/ptracker/tasks" element={<ProtectedRoute><PtrackerTasks /></ProtectedRoute>} />
      <Route path="/ptracker/shoots" element={<ProtectedRoute><PtrackerShoots /></ProtectedRoute>} />
      <Route path="/ptracker/calendar" element={<ProtectedRoute><PtrackerCalendar /></ProtectedRoute>} />
      <Route path="/ptracker/allocation" element={<ProtectedRoute><PtrackerAllocation /></ProtectedRoute>} />
      <Route path="/ptracker/settings" element={<ProtectedRoute><PtrackerSettings /></ProtectedRoute>} />
      <Route path="/ptracker/productivity" element={<ProtectedRoute><PtrackerProductivity /></ProtectedRoute>} />
      <Route path="/ptracker/attendance" element={<ProtectedRoute><PtrackerAttendance /></ProtectedRoute>} />
      <Route path="/ptracker/risks" element={<ProtectedRoute><PtrackerRisks /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route path="/team/:id" element={<TeamMemberPage />} />
        <Route path="/admin/bugs" element={<ProtectedRoute allowedRoles={["admin"]}><AdminBugsPage /></ProtectedRoute>} />
        {/* Admin tasks queue — anyone signed in can view; mutate gated server-side */}
        <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasksPage /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <DMShell>
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <HintsProvider>
        <AppLayout>
          <AppRouter />
        </AppLayout>
              <Toaster 
          position="top-right" 
          richColors 
          theme="dark"
          toastOptions={{
            style: {
              background: '#121212',
              border: '1px solid #27272A',
              color: '#EDEDED',
            },
          }}
        />
        <BugReportWidget />
        <AdminTaskWidget />
        <ShortcutsHelp />
        <GlobalBrainDump />
        <style>{`
          [data-emergent-badge], 
          [class*="emergent"], 
          [id*="emergent"],
          div[style*="Made with Emergent"],
          a[href*="emergent"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
        `}</style>
        </HintsProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
    </DMShell>
  );
}

export default App;
